import { ArrowDown, ArrowUp, Ruler } from 'iconsax-reactjs';
import { FIB_TIMEFRAMES, retracementOf, timeframeShort } from '@pipyar/core';
import { WidgetCard } from '@/components/WidgetCard';
import { useSymbol } from '@/lib/useSymbol';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useQuote } from '@/features/alerts/api';
import { useAutoSwings } from './api';

/**
 * The Fibonacci tile on the dashboard: the detector's swing on each
 * timeframe and how far price has retraced it.
 */
export function FibonacciWidget() {
  const { symbol, isLoading: symbolLoading } = useSymbol();
  const { t } = useI18n();
  const auto = useAutoSwings(symbol ?? null);
  const quote = useQuote(symbol ?? undefined);
  const price = quote.data?.price ?? null;
  const digits = auto.data?.digits ?? 2;
  const loading = symbolLoading || auto.isPending;

  return (
    <WidgetCard
      to="/fibonacci"
      icon={<Ruler size={16} variant="Bold" />}
      title={t('fib.title')}
      module="fibonacci"
    >
      {loading ? (
        <p className="p-3 text-xs text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <ul className="divide-y divide-border/60">
          {FIB_TIMEFRAMES.map((tf) => {
            const all = auto.data?.swings[tf] ?? [];
            const s = all[0] ?? null;
            // The smallest scale still standing: the swing price is working on now.
            const live = all.find((x) => !x.invalid) ?? null;
            const retr = live && price !== null ? retracementOf(live, price) : null;
            return (
              <li key={tf} className="flex items-center gap-2 px-3 py-2 text-xs">
                <span className="w-8 shrink-0 font-medium">{timeframeShort(tf)}</span>
                {live ? (
                  <>
                    <span
                      className={cn(
                        'flex items-center gap-0.5 rounded px-1 py-0.5 text-[11px] font-medium',
                        live.direction === 'up' ? 'bg-bull/15 text-bull' : 'bg-bear/15 text-bear',
                      )}
                    >
                      {live.direction === 'up' ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
                      {live.running ? t('fib.running') : `${live.start.price.toFixed(digits)}→${live.end.price.toFixed(digits)}`}
                    </span>
                    <span className="tabular ms-auto">
                      {retr === null ? '—' : `${Math.round(retr * 1000) / 10}%`}
                    </span>
                  </>
                ) : (
                  <span className="ms-auto text-muted-foreground">{s?.invalid ? t('common.none') : '—'}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <p className="mt-auto px-3 py-2 text-[10px] leading-snug text-muted-foreground">{t('fib.widgetNote')}</p>
    </WidgetCard>
  );
}
